import React, { useState } from "react";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarDays } from "lucide-react";

export const todayRome = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Rome" });

export function DateRangeField({ value, onChange, onDone, min, placeholder = "Qualsiasi data", testId, className = "" }) {
  const [open, setOpen] = useState(false);
  const range = { from: value?.from ? parseISO(value.from) : undefined, to: value?.to ? parseISO(value.to) : undefined };
  const fmt = (d) => format(d, "dd/MM/yyyy");
  const label = range.from ? (range.to && value.to !== value.from ? `${fmt(range.from)} – ${fmt(range.to)}` : fmt(range.from)) : placeholder;
  const iso = (d) => (d ? format(d, "yyyy-MM-dd") : "");
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) onDone?.(value); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} className={`inline-flex items-center gap-2 text-left ${className} ${value?.from ? "text-navy" : "text-slate-400"}`}>
          <CalendarDays className="w-4 h-4 text-gold shrink-0"/><span className="truncate">{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto max-w-[calc(100vw-16px)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto overscroll-contain p-0" align="start" collisionPadding={8} data-testid={testId ? `${testId}-popover` : undefined}>
        <Calendar mode="range" fixedWeeks locale={it} weekStartsOn={1} selected={range} defaultMonth={range.from || (min ? parseISO(min) : undefined)}
          disabled={min ? { before: parseISO(min) } : undefined}
          onSelect={(r) => { const next = { from: iso(r?.from), to: iso(r?.to || r?.from) }; onChange(r?.from ? next : null); if (r?.from && r?.to && r.to > r.from) { setOpen(false); onDone?.(next); } }} />
        <div className="flex flex-wrap items-center justify-between gap-3 px-3 pb-3 text-xs text-slate-500 max-w-[300px]">
          <span className="basis-full">Scegli un giorno o un intervallo (primo e ultimo giorno)</span>
          <button type="button" data-testid={testId ? `${testId}-clear` : undefined} onClick={() => { onChange(null); setOpen(false); onDone?.(null); }} className="text-gold font-semibold hover:underline shrink-0">Cancella</button>
          <button type="button" data-testid={testId ? `${testId}-done` : undefined} onClick={() => { setOpen(false); onDone?.(value); }} className="btn-pill-gold !py-1 !px-3 text-xs shrink-0">OK</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function DateField({ value, onChange, min, placeholder = "gg/mm/aaaa", testId, className = "", allFree = [], someFree = [], legend }) {
  const [open, setOpen] = useState(false);
  const selected = value ? parseISO(value) : undefined;
  const minDate = min ? parseISO(min) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} className={`inline-flex items-center gap-2 text-left ${className} ${value ? "text-navy" : "text-slate-400"}`}>
          <CalendarDays className="w-4 h-4 text-gold shrink-0"/>{selected ? format(selected, "dd/MM/yyyy") : placeholder}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto max-w-[calc(100vw-16px)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto overscroll-contain p-0" align="start" collisionPadding={8} data-testid={testId ? `${testId}-popover` : undefined}>
        <Calendar mode="single" fixedWeeks locale={it} weekStartsOn={1} selected={selected} defaultMonth={selected || minDate}
          disabled={minDate ? { before: minDate } : undefined}
          modifiers={{ allFree: allFree.map((d) => parseISO(d)), someFree: someFree.map((d) => parseISO(d)) }}
          modifiersClassNames={{ allFree: "[&:not(.day-outside)]:!bg-emerald-500 [&:not(.day-outside)]:!text-white font-bold rounded-md", someFree: "[&:not(.day-outside)]:!bg-emerald-100 [&:not(.day-outside)]:!text-emerald-900 rounded-md" }}
          onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }} />
        {legend && <div className="px-3 pb-3 -mt-1 text-[11px] text-slate-600 space-y-1" data-testid={testId ? `${testId}-legend` : undefined}>{legend}</div>}
      </PopoverContent>
    </Popover>
  );
}
